import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

interface WorkbookSheetDialogProps {
  open: boolean;
  fileBase: string;
  sheetNames: string[];
  selectedSheet: string;
  append: boolean;
  onSelect: (name: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  onImportAll?: () => void;
}

const WorkbookSheetDialog: React.FC<WorkbookSheetDialogProps> = ({
  open,
  fileBase,
  sheetNames,
  selectedSheet,
  append,
  onSelect,
  onClose,
  onConfirm,
  onImportAll,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="docs-sheet-title">
    <DialogTitle id="docs-sheet-title">가져올 시트 선택</DialogTitle>
    <DialogContent>
      <Typography fontWeight={700} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
        {fileBase} · 시트 {sheetNames.length}개
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {onImportAll
          ? append
            ? '시트 하나 또는 전체 시트의 행을 이 문서에 추가합니다. 같은 이름의 열끼리 맞추고, 없는 열은 새로 만듭니다.'
            : '시트 하나를 선택하거나 전체 시트를 한 번에 가져올 수 있습니다. 각 시트는 파일명 (시트명)으로 구분된 별도 문서로 추가됩니다.'
          : '업로드한 엑셀에 여러 시트가 있습니다. 가져올 시트를 선택하세요.'}
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel id="docs-sheet-select-label">시트</InputLabel>
        <Select
          labelId="docs-sheet-select-label"
          label="시트"
          value={selectedSheet}
          onChange={(event) => onSelect(event.target.value)}
        >
          {sheetNames.map((name) => (
            <MenuItem
              key={name}
              value={name}
              sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            >
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {onImportAll && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          빈 시트나 읽지 못한 시트는 건너뛰고 결과에 표시합니다.
        </Typography>
      )}
    </DialogContent>
    <DialogActions
      sx={{
        p: 2,
        gap: 1,
        flexWrap: 'wrap',
        '& > :not(style) ~ :not(style)': { ml: 0 },
        '& .MuiButton-root': { minHeight: 44 },
      }}
    >
      <Button onClick={onClose}>취소</Button>
      <Button
        variant={onImportAll ? 'outlined' : 'contained'}
        onClick={onConfirm}
        disabled={!selectedSheet}
      >
        {onImportAll ? '선택 시트 가져오기' : '가져오기'}
      </Button>
      {onImportAll && (
        <Button
          variant="contained"
          onClick={onImportAll}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          전체 시트 가져오기 ({sheetNames.length}개)
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

export default WorkbookSheetDialog;
